import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import { clearAuth } from "../../../utils/authStorage";
import {
  APPLICATION_STATUS_CHIPS,
  APP_LIST_LIMIT,
  APP_LIST_LIMIT_OPTIONS,
  KYC_BADGE,
  STATUS_BADGE,
  formatDateTime,
  isUnauthorized,
  kycLabel,
} from "./masterApplicationUtils";
import "../Home/Homepage.css";
import "./CopyTradingManagementPage.css";
import "./MasterApplicationsPage.css";

const EMPTY_PAGINATION = {
  page: 1,
  limit: APP_LIST_LIMIT,
  total: 0,
  total_pages: 1,
};

const MasterApplicationsPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(APP_LIST_LIMIT);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 700);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, pageLimit]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page,
        limit: pageLimit,
        status: statusFilter || "all",
      };
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await AdminService.getCopyTradingMasterApplications(params);
      if (isUnauthorized(res)) {
        clearAuth();
        window.location.href = "/";
        return;
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list);
      const pag = res?.pagination || {};
      setPagination({
        page: pag.page || page,
        limit: pag.limit || pageLimit,
        total: typeof pag.total === "number" ? pag.total : list.length,
        total_pages: Math.max(1, Number(pag.total_pages) || 1),
      });
      if (res?.success === false) {
        setError(res?.message || "Failed to fetch master applications");
      }
    } catch (err) {
      setRows([]);
      setError(err?.message || "Failed to fetch master applications");
    } finally {
      setLoading(false);
    }
  }, [page, pageLimit, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openView = (applicationId) => {
    if (!applicationId) return;
    navigate(`/dashboard/copy-trading/master-applications/${applicationId}`);
  };

  const pageStart = rows.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const pageEnd = rows.length === 0 ? 0 : pageStart + rows.length - 1;
  const hasFilters = Boolean(debouncedSearch) || statusFilter !== "all";

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <Link to="/dashboard/copy-trading">Copy Trading</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Master Applications</span>
          </p>
          <h1 className="dashboard-hero__title">Master Applications</h1>
          <p className="dashboard-hero__subtitle">
            Become-a-master leads. Contact the person, then promote them on Copy Trading Management.
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-inbox" />
        </div>
      </section>

      <div className="dashboard-body">
        <section className="dashboard-activity-section">
          <div className="dashboard-activity-header copy-trading-header">
            <div className="copy-trading-header__left">
              <div className="dashboard-activity-header__icon">
                <i className="fas fa-user-plus" />
              </div>
              <div>
                <h2 className="dashboard-activity-header__title">Applications</h2>
                <p className="copy-trading-header__hint">
                  Submitting this form does not create a master account. Promote after you call them.
                </p>
              </div>
            </div>
            <div className="copy-trading-header__actions">
              <span className="copy-trading-stat">
                <strong>{rows.length}</strong> on page
                {pagination.total != null ? (
                  <>
                    {" "}
                    · <strong>{pagination.total}</strong> total
                  </>
                ) : null}
              </span>
              <button
                type="button"
                className="copy-trading-refresh-btn"
                onClick={fetchRows}
                disabled={loading}
              >
                <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>

          <div className="ma-filters">
            <input
              type="search"
              className="ma-search"
              placeholder="Name, email, mobile, telegram, UUID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="copy-trading-tabs" role="tablist" aria-label="Application status">
              {APPLICATION_STATUS_CHIPS.map((chip) => (
                <button
                  key={chip.value}
                  type="button"
                  className={`copy-trading-tab ${
                    statusFilter === chip.value ? "copy-trading-tab--active" : ""
                  }`}
                  onClick={() => setStatusFilter(chip.value)}
                >
                  {chip.label}
                </button>
              ))}
            </div>
            <label className="copy-trading-page-limit">
              Rows
              <select
                value={pageLimit}
                onChange={(e) => setPageLimit(Number(e.target.value))}
                className="copy-trading-page-limit__select"
              >
                {APP_LIST_LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {loading ? (
            <div className="copy-trading-table__empty">
              <i className="fas fa-spinner fa-spin" /> Loading…
            </div>
          ) : error ? (
            <div className="ma-empty">
              <p>{error}</p>
              <button type="button" className="copy-trading-refresh-btn" onClick={fetchRows}>
                Retry
              </button>
            </div>
          ) : rows.length === 0 ? (
            <div className="ma-empty">
              <i className="fas fa-inbox" />
              <p>No master applications found</p>
              {hasFilters ? (
                <button
                  type="button"
                  className="copy-trading-refresh-btn"
                  onClick={() => {
                    setSearch("");
                    setDebouncedSearch("");
                    setStatusFilter("all");
                  }}
                >
                  Clear filters
                </button>
              ) : null}
            </div>
          ) : (
            <>
              <div className="dashboard-content-inner copy-trading-content">
                <div className="table-wrap copy-trading-table-wrap">
                  <table className="data-table copy-trading-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Telegram</th>
                        <th>Status</th>
                        <th>Submitted</th>
                        <th>KYC</th>
                        <th>Already a master?</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const id = row.applicationId || row._id;
                        const status = String(row.status || "").toLowerCase();
                        const kyc = Number(row.kycVerified);
                        return (
                          <tr
                            key={id}
                            onClick={() => openView(id)}
                            style={{ cursor: "pointer" }}
                          >
                            <td>
                              <div className="copy-trading-user">
                                <strong>{row.fullName || "—"}</strong>
                                <span>{row.uuid || "—"}</span>
                              </div>
                            </td>
                            <td>{row.email || "—"}</td>
                            <td>
                              {[row.countryCode, row.mobileNumber].filter(Boolean).join(" ") || "—"}
                            </td>
                            <td>{row.telegram || "—"}</td>
                            <td>
                              <span className={`badge ${STATUS_BADGE[status] || ""}`}>
                                {status || "—"}
                              </span>
                            </td>
                            <td>{formatDateTime(row.submittedAt)}</td>
                            <td>
                              <span className={`badge ${KYC_BADGE[kyc] || "badge--pending"}`}>
                                {kycLabel(kyc)}
                              </span>
                            </td>
                            <td>{row.isMasterAccount ? "Yes" : "No"}</td>
                            <td className="copy-trading-table__actions" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="copy-trading-view-btn"
                                onClick={() => openView(id)}
                              >
                                <i className="fas fa-eye" /> View
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="copy-trading-pagination">
                <span className="copy-trading-pagination__info">
                  Showing {pageStart}–{pageEnd}
                  {pagination.total != null ? ` of ${pagination.total}` : ""}
                </span>
                <div className="copy-trading-pagination__buttons">
                  <button
                    type="button"
                    className="copy-trading-pagination__btn"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="copy-trading-pagination__btn"
                    disabled={page >= pagination.total_pages || loading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
};

export default MasterApplicationsPage;
